<script setup>
import { ref, onMounted, computed } from 'vue';
import { useApi } from '@/composables/useApi.js';

const { get, post, put, del } = useApi();

// State
const technicians = ref([]);
const freshdeskChoices = ref([]);
const isLoading = ref(false);
const isSyncing = ref(false);
const syncResult = ref(null);
const error = ref(null);
const showForm = ref(false);
const editingId = ref(null);

// Form data
const formData = ref({
  name: '',
  email: '',
  phone: '',
  freshdeskFieldValue: '',
  notes: '',
  skills: {
    vse: false,
    chlad: false,
    mar: false,
    el: false,
    voda: false
  },
  region: '',
  address: ''
});

// Known Freshdesk agents (auto-fill email)
const knownAgents = ['Pavel', 'Lukáš', 'Dan', 'Karel', 'Martin', 'Ivo', 'Jan'];

// Smart name handler - first word only, rest to notes
function handleNameInput() {
  const fullName = formData.value.name;
  const parts = fullName.trim().split(/\s+/);
  const firstName = parts[0] || '';
  const restOfName = parts.slice(1).join(' ');

  // If there's more than first name, append to notes and trim name
  if (restOfName) {
    const existingNotes = formData.value.notes || '';
    if (!existingNotes.includes(fullName)) {
      formData.value.notes = existingNotes
        ? `${existingNotes}\nCelé jméno: ${fullName}`
        : `Celé jméno: ${fullName}`;
    }
    formData.value.name = firstName;
  }

  // Auto-fill email if matches known agent
  const matchedAgent = knownAgents.find(
    a => a.toLowerCase() === firstName.toLowerCase()
  );
  if (matchedAgent && !formData.value.email) {
    formData.value.email = `${firstName.toLowerCase()}@hotjet.cz`;
  }
}

// Computed
const formTitle = computed(() => editingId.value ? 'Upravit technika' : 'Novy technik');

// Helper to get active skills as array
function getSkills(tech) {
  if (!tech.skills) return [];
  const skills = typeof tech.skills === 'string' ? JSON.parse(tech.skills) : tech.skills;
  return Object.entries(skills)
    .filter(([_, active]) => active)
    .map(([skill]) => skill);
}

// Methods
async function loadTechnicians() {
  isLoading.value = true;
  error.value = null;

  try {
    const data = await get('/api/technicians/list?include_inactive=true');
    technicians.value = data.technicians || [];
  } catch (err) {
    error.value = err.message;
    console.error('Error loading technicians:', err);
  } finally {
    isLoading.value = false;
  }
}

async function loadFreshdeskChoices() {
  try {
    const data = await get('/api/freshdesk/fields');
    if (data.technikField?.choices) {
      freshdeskChoices.value = data.technikField.choices;
    }
  } catch (err) {
    console.error('Error loading Freshdesk choices:', err);
  }
}

function openForm(technician = null) {
  if (technician) {
    editingId.value = technician.id;
    // Parse skills from DB (might be string or object)
    let skills = { vse: false, chlad: false, mar: false, el: false, voda: false };
    if (technician.skills) {
      const parsed = typeof technician.skills === 'string'
        ? JSON.parse(technician.skills)
        : technician.skills;
      skills = { ...skills, ...parsed };
    }
    formData.value = {
      name: technician.name || '',
      email: technician.email || '',
      phone: technician.phone || '',
      freshdeskFieldValue: technician.freshdesk_field_value || '',
      notes: technician.notes || '',
      skills,
      region: technician.region || '',
      address: technician.address || ''
    };
  } else {
    editingId.value = null;
    formData.value = {
      name: '',
      email: '',
      phone: '',
      freshdeskFieldValue: '',
      notes: '',
      skills: { vse: false, chlad: false, mar: false, el: false, voda: false },
      region: '',
      address: ''
    };
  }
  showForm.value = true;
}

function closeForm() {
  showForm.value = false;
  editingId.value = null;
}

async function saveForm() {
  isLoading.value = true;
  error.value = null;

  try {
    if (editingId.value) {
      await put(`/api/technicians/update?id=${editingId.value}`, formData.value);
    } else {
      await post('/api/technicians/create', formData.value);
    }

    await loadTechnicians();
    closeForm();
  } catch (err) {
    error.value = err.message;
    console.error('Error saving technician:', err);
  } finally {
    isLoading.value = false;
  }
}

async function toggleActive(technician) {
  try {
    await put(`/api/technicians/update?id=${technician.id}`, { isActive: !technician.is_active });
    await loadTechnicians();
  } catch (err) {
    error.value = err.message;
    console.error('Error toggling technician:', err);
  }
}

async function deleteTechnician(technician) {
  if (!confirm(`Opravdu deaktivovat technika "${technician.name}"?`)) return;

  try {
    await del(`/api/technicians/delete?id=${technician.id}`);
    await loadTechnicians();
  } catch (err) {
    error.value = err.message;
    console.error('Error deleting technician:', err);
  }
}

async function syncFromFreshdesk() {
  isSyncing.value = true;
  syncResult.value = null;
  error.value = null;

  try {
    // Load fresh choices from Freshdesk
    const fieldsData = await get('/api/freshdesk/fields');
    const choices = fieldsData.technikField?.choices || [];

    if (choices.length === 0) {
      syncResult.value = { added: 0, skipped: 0, message: 'Zadne hodnoty v poli Technik' };
      return;
    }

    // Get existing freshdesk values
    const existingValues = new Set(
      technicians.value
        .filter(t => t.freshdesk_field_value)
        .map(t => t.freshdesk_field_value.toLowerCase())
    );

    let added = 0;
    let skipped = 0;

    for (const choice of choices) {
      if (existingValues.has(choice.toLowerCase())) {
        skipped++;
        continue;
      }

      // Create new technician
      try {
        await post('/api/technicians/create', {
          name: choice,
          email: `${choice.toLowerCase().replace(/\s+/g, '.')}@hotjet.cz`,
          freshdeskFieldValue: choice
        });
        added++;
      } catch {
        // Skip failed creates silently (same behavior as before)
      }
    }

    syncResult.value = { added, skipped, message: `Pridano ${added} techniku, preskoceno ${skipped}` };
    await loadTechnicians();
  } catch (err) {
    error.value = err.message;
    console.error('Error syncing from Freshdesk:', err);
  } finally {
    isSyncing.value = false;
  }
}

// Init
onMounted(() => {
  loadTechnicians();
  loadFreshdeskChoices();
});
</script>

<template>
  <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
      <div>
        <h2 class="text-lg font-semibold text-gray-900">Technici</h2>
        <p class="text-sm text-gray-500">Sprava servisnich techniku</p>
      </div>
      <div class="flex items-center gap-2">
        <button
          @click="syncFromFreshdesk"
          :disabled="isSyncing"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50"
        >
          <svg v-if="isSyncing" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {{ isSyncing ? 'Nacitam...' : 'Nacist z Freshdesku' }}
        </button>
        <button
          @click="openForm()"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Pridat technika
        </button>
      </div>
    </div>

    <!-- Sync result -->
    <div v-if="syncResult" class="px-6 py-3 bg-blue-50 text-blue-700 text-sm flex items-center justify-between">
      <span>{{ syncResult.message }}</span>
      <button @click="syncResult = null" class="text-blue-500 hover:text-blue-700">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Error -->
    <div v-if="error" class="px-6 py-3 bg-red-50 text-red-700 text-sm">
      {{ error }}
    </div>

    <!-- Loading -->
    <div v-if="isLoading && technicians.length === 0" class="px-6 py-12 text-center text-gray-500">
      Nacitam...
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Technik</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kontakt</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Odbornosti</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Oblast</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          <tr v-for="tech in technicians" :key="tech.id" :class="{ 'bg-gray-50 opacity-60': !tech.is_active }">
            <td class="px-6 py-4">
              <div class="font-medium text-gray-900">{{ tech.name }}</div>
              <div v-if="tech.notes" class="text-sm text-gray-500 truncate max-w-xs">{{ tech.notes }}</div>
            </td>
            <td class="px-6 py-4">
              <div class="text-sm text-gray-900">{{ tech.email }}</div>
              <div v-if="tech.phone" class="text-sm text-gray-500">{{ tech.phone }}</div>
            </td>
            <td class="px-6 py-4">
              <div class="flex flex-wrap gap-1">
                <span v-if="getSkills(tech).includes('vse')" class="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Vše</span>
                <span v-if="getSkills(tech).includes('chlad')" class="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">Chlad</span>
                <span v-if="getSkills(tech).includes('mar')" class="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full">MaR</span>
                <span v-if="getSkills(tech).includes('el')" class="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">Elektro</span>
                <span v-if="getSkills(tech).includes('voda')" class="px-2 py-0.5 bg-cyan-100 text-cyan-800 text-xs rounded-full">Voda</span>
                <span v-if="getSkills(tech).length === 0" class="text-gray-400 text-xs">-</span>
              </div>
            </td>
            <td class="px-6 py-4">
              <div class="text-sm text-gray-900">{{ tech.region || '-' }}</div>
            </td>
            <td class="px-6 py-4">
              <span
                :class="tech.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                class="px-2 py-1 text-xs rounded-full"
              >
                {{ tech.is_active ? 'Aktivni' : 'Neaktivni' }}
              </span>
            </td>
            <td class="px-6 py-4 text-right space-x-2">
              <button
                @click="openForm(tech)"
                class="text-blue-600 hover:text-blue-800 text-sm"
              >
                Upravit
              </button>
              <button
                @click="toggleActive(tech)"
                class="text-gray-600 hover:text-gray-800 text-sm"
              >
                {{ tech.is_active ? 'Deaktivovat' : 'Aktivovat' }}
              </button>
            </td>
          </tr>
          <tr v-if="technicians.length === 0">
            <td colspan="6" class="px-6 py-12 text-center text-gray-500">
              Zadni technici. Klikni na "Pridat technika" pro vytvoreni.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal Form -->
    <div v-if="showForm" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-4">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 my-auto">
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900">{{ formTitle }}</h3>
          <button @click="closeForm" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="saveForm" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Jmeno *</label>
            <input
              v-model="formData.name"
              @blur="handleNameInput"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Jan Novak (zadejte celé jméno)"
            />
            <p class="mt-1 text-xs text-gray-500">Zadejte celé jméno - uloží se pouze křestní, zbytek do poznámek</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              v-model="formData.email"
              type="email"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="jan.novak@example.com"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
            <input
              v-model="formData.phone"
              type="tel"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="+420 123 456 789"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Freshdesk hodnota</label>
            <select
              v-if="freshdeskChoices.length > 0"
              v-model="formData.freshdeskFieldValue"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">-- Vyberte --</option>
              <option v-for="choice in freshdeskChoices" :key="choice" :value="choice">
                {{ choice }}
              </option>
            </select>
            <input
              v-else
              v-model="formData.freshdeskFieldValue"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Hodnota z Freshdesk pole Technik"
            />
            <p class="mt-1 text-xs text-gray-500">Propojeni s polem "Technik" ve Freshdesku</p>
          </div>

          <!-- Skills checkboxes -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Odbornosti</label>
            <div class="flex flex-wrap gap-3">
              <label class="inline-flex items-center">
                <input type="checkbox" v-model="formData.skills.vse" class="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                <span class="ml-2 text-sm text-gray-700">Vše</span>
              </label>
              <label class="inline-flex items-center">
                <input type="checkbox" v-model="formData.skills.chlad" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span class="ml-2 text-sm text-gray-700">Chlad</span>
              </label>
              <label class="inline-flex items-center">
                <input type="checkbox" v-model="formData.skills.mar" class="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                <span class="ml-2 text-sm text-gray-700">MaR</span>
              </label>
              <label class="inline-flex items-center">
                <input type="checkbox" v-model="formData.skills.el" class="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500" />
                <span class="ml-2 text-sm text-gray-700">Elektro</span>
              </label>
              <label class="inline-flex items-center">
                <input type="checkbox" v-model="formData.skills.voda" class="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500" />
                <span class="ml-2 text-sm text-gray-700">Voda</span>
              </label>
            </div>
          </div>

          <!-- Region -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Oblast</label>
            <input
              v-model="formData.region"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Praha, Střední Čechy..."
            />
          </div>

          <!-- Address -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Adresa</label>
            <textarea
              v-model="formData.address"
              rows="2"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Ulice, PSČ Město..."
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Poznamky</label>
            <textarea
              v-model="formData.notes"
              rows="2"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Interni poznamky..."
            ></textarea>
          </div>

          <div class="flex justify-end gap-3 pt-4">
            <button
              type="button"
              @click="closeForm"
              class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
            >
              Zrusit
            </button>
            <button
              type="submit"
              :disabled="isLoading"
              class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              {{ isLoading ? 'Ukladam...' : 'Ulozit' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
